import addMediaElementOperation from 'editor/src/store/design/operation/addMediaElementOperation';
import updateMediaElementByUuidOperation from 'editor/src/store/design/operation/updateMediaElementByUuidOperation';
import { DesignData, MediaElement, MediaImage, Page } from 'editor/src/store/design/types';
import { GalleryImage } from 'editor/src/store/gallery/types';
import type { ThunkDispatch } from 'editor/src/store/hooks';

import getCenteredImagePosition from 'editor/src/util/2d/getCenteredImagePosition';
import findNotUsedImages from 'editor/src/util/autofill/findNotUsedImages';
import applyFillImageToElementPlaceholder from 'editor/src/util/design/applyFillImageToElementPlaceholder';
import isImagePlaceholder from 'editor/src/util/design/isImagePlaceholder';
import { elementUuids } from 'editor/src/util/uuids';

import createPageLayout, { mirrorLayout } from './createPageLayout';
import groupImages from './groupImages';

type PageTarget =
  | { kind: 'layout'; spreadIndex: number; page: Page }
  | { kind: 'placeholders'; spreadIndex: number; placeholders: MediaImage[] };

// A spread keeps all its elements on its first page, positioned in spread coordinates.
const getPageMedia = (spreadMedia: MediaElement[], page: Page) =>
  spreadMedia.filter((element) => {
    const centerX = element.type === 'line' ? (element.x1 + element.x2) / 2 : element.x + element.width / 2;
    return centerX >= page.x && centerX < page.x + page.width;
  });

// A page with empty image placeholders is filled through those placeholders, so the seller's layout and
// static elements are kept. A page with elements but no empty placeholder is already designed and is left alone.
const getPageTargets = (designData: DesignData, spreadIndexesToIgnore: number[]): PageTarget[] =>
  designData.spreads.flatMap((spread, spreadIndex) => {
    if (spreadIndexesToIgnore.includes(spreadIndex)) {
      return [];
    }
    const spreadMedia = spread.pages[0].groups.media ?? [];

    return spread.pages.flatMap((page): PageTarget[] => {
      if (page.groups.blank || !page.groups.mediabox?.length) {
        return [];
      }
      const pageMedia = getPageMedia(spreadMedia, page);
      if (!pageMedia.length) {
        return [{ kind: 'layout', spreadIndex, page }];
      }
      const placeholders = pageMedia.filter(isImagePlaceholder);
      return placeholders.length ? [{ kind: 'placeholders', spreadIndex, placeholders }] : [];
    });
  });

// Placeholder pages take exactly as many images as they have slots, so they draw across group boundaries.
const takeImages = (groups: GalleryImage[][], count: number) => {
  const images: GalleryImage[] = [];
  while (images.length < count && groups.length) {
    images.push(...groups[0].splice(0, count - images.length));
    if (!groups[0].length) {
      groups.shift();
    }
  }
  return images;
};

const applyDynamicLayout =
  (designData: DesignData, galleryImages: GalleryImage[], spreadIndexesToIgnore: number[], margin: number) =>
  (dispatch: ThunkDispatch) => {
    const pageTargets = getPageTargets(designData, spreadIndexesToIgnore);
    const groupedImages = groupImages(findNotUsedImages(galleryImages, designData), pageTargets.length);

    // Placeholder pages can take fewer images than their group, so assign every image before placing any
    const placeholderFills: { placeholders: MediaImage[]; images: GalleryImage[] }[] = [];
    const layoutFills: { spreadIndex: number; page: Page; images: GalleryImage[] }[] = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const target of pageTargets) {
      if (!groupedImages.length) {
        break;
      }
      if (target.kind === 'placeholders') {
        placeholderFills.push({
          placeholders: target.placeholders,
          images: takeImages(groupedImages, target.placeholders.length),
        });
        continue;
      }
      const images = groupedImages.shift();
      if (images) {
        layoutFills.push({ spreadIndex: target.spreadIndex, page: target.page, images });
      }
    }

    // Images left once every page has its share go to the layout pages rather than being dropped
    groupedImages.flat().forEach((image, index) => {
      if (layoutFills.length) {
        layoutFills[index % layoutFills.length].images.push(image);
      }
    });

    placeholderFills.forEach(({ placeholders, images }) => {
      images.forEach((image, index) => {
        const placeholder = placeholders[index];
        dispatch(
          updateMediaElementByUuidOperation(placeholder.uuid, applyFillImageToElementPlaceholder(placeholder, image)),
        );
      });
    });

    // Track used layout direction per spread
    const usedLayoutKeys = new Map<number, Set<string>>();

    layoutFills.forEach(({ spreadIndex, page, images }) => {
      const pageLayout = createPageLayout(images, page, { margin });
      const spreadLayoutKeys = usedLayoutKeys.get(spreadIndex) ?? new Set<string>();
      usedLayoutKeys.set(spreadIndex, spreadLayoutKeys);

      // when the layout direction is already used in this spread, mirror it for visual beauty
      let finalLayout = pageLayout;
      if (spreadLayoutKeys.has(pageLayout.direction)) {
        finalLayout = mirrorLayout(pageLayout, page.width, page.height);
      }
      spreadLayoutKeys.add(pageLayout.direction);

      finalLayout.slots.forEach((slot) => {
        const { image, x, y, width, height } = slot;

        const imageElement: MediaImage = {
          type: 'image',
          group: '',
          name: image.name,
          x: page.x + x,
          y: page.y + y,
          width,
          height,
          r: 0,
          url: image.url,
          px: 0,
          py: 0,
          pw: width,
          ph: height,
          pr: 0,
          uuid: elementUuids.generate(),
          imageId: image.id,
          locked: undefined,
        };

        const { px, py, pw, ph } = getCenteredImagePosition(imageElement, image);
        imageElement.px = px;
        imageElement.py = py;
        imageElement.pw = pw;
        imageElement.ph = ph;

        dispatch(addMediaElementOperation(spreadIndex, 0, imageElement));
      });
    });
  };

export default applyDynamicLayout;
